/**
 * IMBEE-10010 — the post-chat rating's path to the server.
 *
 * Extracted from App.tsx so the production path is the tested one. IMBEE-9629 wired the rating
 * up but left the whole submission untested: `PostChatSurvey.test.tsx` proves the component
 * hands a star count to its callback, and nothing beyond that point was covered at all.
 *
 * What needed covering is not the request but WHICH session the request names. The end-chat flow
 * clears the session id and mints a fresh visitor token immediately after the visitor answers
 * "no", and the rating is submitted seconds later — so by submit time `apiClient` no longer holds
 * the pair the rating belongs to. The values have to be captured beforehand and carried.
 */

/** The session a rating belongs to, captured before the end-chat reset clears it. */
export type EndedSession = {
    sessionId: string;
    visitorToken: string;
};

/**
 * What became of a rating. `saved: false` is not an error to throw — the visitor has already
 * ended the chat and the confirmation follows either way — but it must not vanish silently
 * either, which is what the previous `catch { console.log }` amounted to.
 */
export type RatingOutcome =
    | { saved: true }
    | { saved: false; reason: 'no-session' | 'request-failed'; error?: unknown };

/** Minimal shape this module needs, so tests do not have to build a whole ApiClient. */
type SurveyClient = {
    sessionId: string | null;
    visitorToken: string | null;
    submitSurvey: (sessionId: string, visitorToken: string, rating: number) => Promise<unknown>;
};

/**
 * Read the session/token pair the rating will belong to.
 *
 * Must be called BEFORE the end-chat reset. In App.tsx that reset is only a few lines below the
 * call site, and it does both halves of the damage: `setSessionId('')` and a freshly generated
 * `visitorToken` written to localStorage. A submission that reads the client at submit time
 * therefore names an empty session and a token the ended session never had.
 */
export const captureEndedSession = (client: SurveyClient): EndedSession => ({
    sessionId: client.sessionId ?? '',
    visitorToken: client.visitorToken ?? '',
});

/**
 * Submit a rating against a previously captured session. Never throws.
 *
 * `no-session` covers the case where the survey pane was somehow reached without the capture
 * having run — today unreachable, because `setIsContactForm(false)` occurs at exactly one place
 * in App.tsx and the capture is three lines above it, but a returned reason is cheaper than
 * relying on that staying true.
 *
 * `request-failed` is the one that actually happens. The endpoint rejects the survey unless the
 * session is already `Ended`, so a failed or slow `endSession` surfaces here as a 400 rather than
 * anywhere nearer its cause.
 */
export const submitCapturedRating = async (
    client: SurveyClient,
    ended: EndedSession | null,
    rating: number,
): Promise<RatingOutcome> => {
    if (!ended || !ended.sessionId) return { saved: false, reason: 'no-session' };
    try {
        await client.submitSurvey(ended.sessionId, ended.visitorToken, rating);
        return { saved: true };
    } catch (error) {
        return { saved: false, reason: 'request-failed', error };
    }
};
